import { computed, effect, inject } from '@angular/core';
import { TransactionStore } from '@app/transaction/store/transaction.store';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom, Observable } from 'rxjs';
import { BaseService } from '@app/services/base/classes/base-service';
import { BaseServiceService } from '@app/services/base/services/base-service.service';
import { StatusBadgeStatus } from '@ui-v2/atoms/status-badge/status-badge.constants';

// Define an interface for services that have updateFilingOption method
export interface ServiceWithFilingOption<T> extends BaseServiceService<T> {
    updateFilingOption(transactionId: string, filingOptionId: string): Observable<T>;
}

export function createServiceStoreFactory<T extends BaseService>(
    serviceName: string,
    ServiceClass: new (data: any) => T,
    serviceApiToken: any,
) {
    type ServiceState = {
        isLoading: boolean;
        service: T | null;
    };

    const defaultState: ServiceState = {
        isLoading: false,
        service: null,
    };

    return signalStore(
        { providedIn: 'root' },
        withState(defaultState),
        withComputed((state) => {
            return {
                active: computed(() => !!state.service()?.isActive()),
                isNotActive: computed(() => !state.service()?.isActive()),
                getStatus: computed(() => state.service()?.status),
                isReadyToSubmit: computed(() => state.service()?.isReadyToSubmit()),
                isSubmitted: computed(() => state.service()?.isSubmitted()),
            };
        }),
        withMethods((store) => {
            const serviceApi: BaseServiceService<T> = inject(serviceApiToken);

            return {
                set(state: Partial<ServiceState>) {
                    patchState(store, state);
                },

                reset() {
                    patchState(store, defaultState);
                },

                async load(transactionId: string) {
                    patchState(store, { isLoading: true });
                    try {
                        const data = await firstValueFrom(serviceApi.get(transactionId));
                        patchState(store, {
                            isLoading: false,
                            service: new ServiceClass(data),
                        });
                    } catch (error) {
                        patchState(store, { isLoading: false });
                    }
                },

                async submit(transactionId: string, submitted: boolean) {
                    patchState(store, { isLoading: true });
                    try {
                        const updated = await firstValueFrom(serviceApi.submit(transactionId, submitted));
                        patchState(store, {
                            isLoading: false,
                            service: new ServiceClass(updated),
                        });
                    } catch (error) {
                        patchState(store, { isLoading: false });
                    }
                },

                async updateActive(transactionId: string, active: boolean) {
                    patchState(store, { isLoading: true });
                    try {
                        const updated = await firstValueFrom(serviceApi.updateActive(transactionId, active));
                        patchState(store, {
                            isLoading: false,
                            service: new ServiceClass(updated),
                        });
                    } catch (error) {
                        patchState(store, { isLoading: false });
                    }
                },

                async updateFilingOption(transactionId: string, filingOptionId: string) {
                    patchState(store, { isLoading: true });
                    try {
                        // Cast to ServiceWithFilingOption to access the updateFilingOption method
                        const serviceWithFilingOption = serviceApi as ServiceWithFilingOption<T>;
                        const updated = await firstValueFrom(serviceWithFilingOption.updateFilingOption(transactionId, filingOptionId));
                        patchState(store, {
                            isLoading: false,
                            service: new ServiceClass(updated),
                        });
                    } catch (error) {
                        patchState(store, { isLoading: false });
                    }
                },
            };
        }),
        withHooks(({ load }) => {
            const transactionStore = inject(TransactionStore);

            effect(
                () => {
                    if (transactionStore.transactionId()) {
                        load(transactionStore.transactionId());
                    }
                },
                { allowSignalWrites: true },
            );

            return {
                onInit() {},
            };
        }),
    );
}
